import {
  ConsentHistoryPrisma,
  ConsentParams,
  ConsentPrisma,
  Paginated,
  PaginatedResponse,
  QueryArgs,
  Sortable,
} from "@ac-expo/api-types";
import { Consent } from "@prisma/client";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import {
  consentQueryKeys,
  ConsentsFilter,
} from "../query-keys/consent.query-keys";

const getConsent = async (organiserId?: string, structureId?: string) => {
  const { data } = await privateAxios.get<Consent>(`/consents/profesionnal`, {
    params: {
      organiserId,
      structureId,
    },
  });
  return data;
};

export const useGetConsentQuery = ({
  organiserId,
  structureId,
  userId,
}: ConsentParams) => {
  return useQuery({
    queryKey: consentQueryKeys.getConsent(organiserId, structureId, userId),
    queryFn: () => getConsent(organiserId, structureId),
    enabled:
      (organiserId !== undefined || structureId !== undefined) &&
      userId !== undefined,
  });
};

const getConsentWithMultipleProfesionnals = async (
  organiserIds?: string[],
  structureIds?: string[]
) => {
  const { data } = await privateAxios.get<Consent[]>(
    `/consents/profesionnals`,
    {
      params: {
        organiserIds,
        structureIds,
      },
    }
  );
  return data;
};

export const useGetConsentWithMultipleProfesionnalsQuery = (
  organiserIds?: string[],
  structureIds?: string[]
) => {
  return useQuery({
    queryKey: consentQueryKeys.getConsentWithMultipleProfesionnals(
      organiserIds,
      structureIds
    ),
    queryFn: () =>
      getConsentWithMultipleProfesionnals(organiserIds, structureIds),
  });
};

const getConsents = async (filters?: Sortable<Paginated<ConsentsFilter>>) => {
  try {
    const res = await privateAxios.get<PaginatedResponse<ConsentPrisma>>(
      `/consents`,
      {
        params: filters,
      }
    );
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetConsentsQuery = (
  filters?: Sortable<Paginated<ConsentsFilter>>,
  options?: QueryArgs<PaginatedResponse<ConsentPrisma>>
) => {
  return useQuery({
    queryKey: consentQueryKeys.lists(filters),
    queryFn: () => getConsents(filters),
    ...options,
  });
};

const getMyConsents = async () => {
  const { data } = await privateAxios.get<ConsentPrisma[]>(`/consents/mine`);
  return data;
};

export const useGetMyConsents = () => {
  return useQuery({
    queryKey: consentQueryKeys.mine(),
    queryFn: getMyConsents,
  });
};

const getConsentHistory = async (
  filters?: Sortable<Paginated<ConsentsFilter>>
) => {
  try {
    const res = await privateAxios.get<PaginatedResponse<ConsentHistoryPrisma>>(
      `/consents/history`,
      {
        params: filters,
      }
    );
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetConsentHistoryQuery = (
  filters?: Sortable<Paginated<ConsentsFilter>>,
  options?: QueryArgs<PaginatedResponse<ConsentHistoryPrisma>>
) => {
  return useQuery({
    queryKey: consentQueryKeys.history(filters),
    queryFn: () => getConsentHistory(filters),
    ...options,
  });
};
